import React, { useEffect, useRef, useState } from 'react';
import { Layer, Shape } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { useMap, TERRAIN_MAP } from '../../contexts/MapContext';
import { getTexture } from '../../lib/textures';

const S = CELL_SIZE;

const TEXTURE_CACHE = {};
function warmTextures() {
  Object.keys(TERRAIN_MAP).forEach((id) => {
    if (!TEXTURE_CACHE[id]) TEXTURE_CACHE[id] = getTexture(id);
  });
}
if (typeof document !== 'undefined') warmTextures();

// Cached patterns so createPattern runs once per (ctx, texture) per paint pass
function drawTerrainCells(ctx, terrain, elevation, gridW, gridH) {
  ctx.imageSmoothingEnabled = false;
  const patternCache = new Map();
  for (let y = 0; y < gridH; y++) {
    for (let x = 0; x < gridW; x++) {
      const idx = y * gridW + x;
      const tid = terrain[idx];
      if (!tid || tid === 'empty') continue;
      const tDef = TERRAIN_MAP[tid];
      if (!tDef) continue;

      const px = x * S;
      const py = y * S;
      const texture = TEXTURE_CACHE[tid] || getTexture(tid);

      if (texture) {
        let pattern = patternCache.get(tid);
        if (!pattern) {
          pattern = ctx.createPattern(texture, 'repeat');
          patternCache.set(tid, pattern);
        }
        // Per-cell offset via translate so we don't rebuild the pattern
        const offsetX = (idx * 7)  % 32;
        const offsetY = (idx * 13) % 32;
        ctx.save();
        ctx.translate(px - offsetX, py - offsetY);
        ctx.fillStyle = pattern;
        ctx.fillRect(offsetX, offsetY, S, S);
        ctx.restore();
      } else {
        if (tDef.color === 'transparent') continue;
        ctx.fillStyle = tDef.color;
        ctx.fillRect(px, py, S, S);
      }

      // 1-px dark cell edge so the grid reads clearly over pixel-art tiles.
      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      ctx.fillRect(px,         py,         S, 1);
      ctx.fillRect(px,         py + S - 1, S, 1);
      ctx.fillRect(px,         py,         1, S);
      ctx.fillRect(px + S - 1, py,         1, S);

      // Elevation overlay + label
      const elev = elevation[idx] || 0;
      if (elev > 0) {
        ctx.fillStyle = `rgba(255,255,255,${Math.min(elev * 0.07, 0.5)})`;
        ctx.fillRect(px, py, S, S);
        // Label
        ctx.save();
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = '#000';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillStyle = '#fff';
        ctx.fillText(`${elev * 5}ft`, px + S / 2, py + S / 2);
        ctx.restore();
      }
    }
  }
}

// Offscreen-canvas bake. Terrain only changes when the DM paints, so
// re-drawing 900+ cells on every pan/zoom is wasted work. We bake a single
// HTMLCanvasElement on terrain/elevation/grid change and blit it via one
// drawImage per Konva paint. iOS Safari handles drawImage far better than
// per-cell fillRect each frame.
//
// Cache invalidation uses reference identity — MapContext only allocates new
// terrain/elevation arrays when data actually changes, so === on the array
// reference is a free O(1) hit on the unchanged path.
const bakeCache = {
  canvas: null,
  terrain: null,
  elevation: null,
  gridW: 0,
  gridH: 0,
};

function getBakedCanvas(terrain, elevation, gridW, gridH) {
  if (
    bakeCache.canvas &&
    bakeCache.terrain === terrain &&
    bakeCache.elevation === elevation &&
    bakeCache.gridW === gridW &&
    bakeCache.gridH === gridH
  ) {
    return bakeCache.canvas;
  }
  const totalW = gridW * S;
  const totalH = gridH * S;
  let cv = bakeCache.canvas;
  if (!cv) {
    cv = document.createElement('canvas');
  }
  if (cv.width !== totalW || cv.height !== totalH) {
    cv.width  = totalW;
    cv.height = totalH;
  } else {
    cv.getContext('2d').clearRect(0, 0, totalW, totalH);
  }
  drawTerrainCells(cv.getContext('2d'), terrain, elevation, gridW, gridH);
  bakeCache.canvas    = cv;
  bakeCache.terrain   = terrain;
  bakeCache.elevation = elevation;
  bakeCache.gridW     = gridW;
  bakeCache.gridH     = gridH;
  return cv;
}

export default function TerrainLayer({ gridW, gridH, bare = false }) {
  const { terrain, elevation } = useMap();
  const [ready, setReady] = useState(typeof document !== 'undefined');
  useEffect(() => { if (!ready) { warmTextures(); setReady(true); } }, [ready]);

  const shapeRef = useRef(null);
  useEffect(() => {
    shapeRef.current?.getLayer()?.batchDraw();
  }, [terrain, elevation, ready, gridW, gridH]);

  if (!ready || !gridW || !gridH) return bare ? null : <Layer listening={false} />;

  const totalW = gridW * S;
  const totalH = gridH * S;

  const scene = (ctx, shape) => {
    const baked = getBakedCanvas(terrain, elevation, gridW, gridH);
    if (baked) ctx.drawImage(baked, 0, 0);
    ctx.fillStrokeShape(shape);
  };

  const shape = (
    <Shape
      ref={shapeRef}
      sceneFunc={scene}
      perfectDrawEnabled={false}
      listening={false}
      x={0} y={0}
      width={totalW} height={totalH}
    />
  );

  if (bare) return shape;
  return <Layer listening={false}>{shape}</Layer>;
}
